import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ThemeContext } from '../context/ThemeContext';
import './Settings.css';

const Settings = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const twitterSectionRef = useRef(null);
  const { theme, availableThemes, changeTheme } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    email: '',
    emailVerified: false,
    accountType: 'regular',
    socialAccounts: {},
    profile: {},
    verifiedAccountsPublic: true
  });
  const [steamId, setSteamId] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verifyingSteam, setVerifyingSteam] = useState(false);
  const [verifyingBluesky, setVerifyingBluesky] = useState(false);
  const [blueskyHandle, setBlueskyHandle] = useState('');
  const [blueskyAppPassword, setBlueskyAppPassword] = useState('');
  const [changingTheme, setChangingTheme] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [accountTypeRequest, setAccountTypeRequest] = useState({
    requestedAccountType: '',
    reason: ''
  });
  const [pendingRequest, setPendingRequest] = useState(null);
  const [submittingAccountTypeChange, setSubmittingAccountTypeChange] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const checkCallbackStatus = useCallback(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');

    if (success === 'steam_linked') {
      setMessage({ type: 'success', text: 'Steam account linked successfully!' });
      fetchSettings();
    } else if (success === 'discord_linked') {
      setMessage({ type: 'success', text: 'Discord account linked successfully!' });
      fetchSettings();
    } else if (success === 'twitter_linked') {
      setMessage({ type: 'success', text: 'Twitter account linked successfully!' });
      fetchSettings();
    } else if (success === 'bluesky_linked') {
      setMessage({ type: 'success', text: 'Bluesky account linked successfully!' });
      fetchSettings();
    } else if (success === 'twitch_linked') {
      setMessage({ type: 'success', text: 'Twitch account linked successfully!' });
      fetchSettings();
    } else if (error) {
      const errorMessages = {
        steam_failed: 'Failed to link Steam account. Please try again.',
        discord_failed: 'Failed to link Discord account. Please try again.',
        twitter_failed: 'Failed to link Twitter account. Please try again.',
        twitter_save_failed: 'Failed to save Twitter account data. Please try again.',
        bluesky_failed: 'Failed to link Bluesky account. Please try again.',
        twitch_failed: 'Failed to link Twitch account. Please try again.',
        steam_already_linked: 'This Steam account is already linked to another user.',
        discord_already_linked: 'This Discord account is already linked to another user.',
        twitter_already_linked: 'Your Twitter account is already linked.',
        bluesky_already_linked: 'This Bluesky account is already linked to another user.',
        twitch_already_linked: 'This Twitch account is already linked to another user.',
        steam_banned: 'This Steam account has been banned and cannot be linked.',
        discord_banned: 'This Discord account has been banned and cannot be linked.',
        twitter_banned: 'This Twitter account has been banned and cannot be linked.',
        bluesky_banned: 'This Bluesky account has been banned and cannot be linked.',
        twitch_banned: 'This Twitch account has been banned and cannot be linked.',
        session_expired: 'Session expired. Please try again.'
      };
      setMessage({ type: 'error', text: errorMessages[error] || 'An error occurred.' });
    }

    // Clear URL parameters
    if (success || error) {
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSettings();
    checkCallbackStatus();
    fetchPendingAccountTypeChange();
  }, [checkCallbackStatus]);

  // Scroll to Twitter section if navigated from notice/toast
  useEffect(() => {
    if (location.state?.scrollToTwitter && twitterSectionRef.current && !loading) {
      setTimeout(() => {
        twitterSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Add a highlight effect
        twitterSectionRef.current.classList.add('highlight-section');
        setTimeout(() => {
          twitterSectionRef.current?.classList.remove('highlight-section');
        }, 2000);
      }, 300);
    }
  }, [location, loading]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/settings', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
        params: {
          _t: Date.now() // Cache busting parameter
        }
      });
      console.log('Settings data received:', response.data.data.socialAccounts);
      setSettings(response.data.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingAccountTypeChange = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/profile/account-type-change/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRequest(response.data.request);
    } catch (error) {
      console.error('Error fetching pending account type change:', error);
    }
  };

  const handleAccountTypeChangeSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!accountTypeRequest.requestedAccountType || !accountTypeRequest.reason.trim()) {
      setMessage({ type: 'error', text: 'Please select an account type and provide a reason' });
      return;
    }

    if (accountTypeRequest.reason.length < 20) {
      setMessage({ type: 'error', text: 'Reason must be at least 20 characters' });
      return;
    }

    setSubmittingAccountTypeChange(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/profile/account-type-change',
        {
          requestedAccountType: accountTypeRequest.requestedAccountType,
          reason: accountTypeRequest.reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: 'Account type change request submitted! It will be reviewed by an admin.' });
      setAccountTypeRequest({ requestedAccountType: '', reason: '' });
      fetchPendingAccountTypeChange();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to submit account type change request'
      });
    } finally {
      setSubmittingAccountTypeChange(false);
    }
  };

  const resendVerificationEmail = async () => {
    setVerifyingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/auth/resend-verification',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: response.data.message });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send verification email'
      });
    } finally {
      setVerifyingEmail(false);
    }
  };

  const verifySteamAccount = async () => {
    if (!steamId.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid Steam ID' });
      return;
    }

    setVerifyingSteam(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/settings/verify-steam',
        { steamId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: response.data.message });
      setSteamId('');
      fetchSettings();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to verify Steam account'
      });
    } finally {
      setVerifyingSteam(false);
    }
  };

  const verifyBlueskyAccount = async () => {
    if (!blueskyHandle.trim()) {
      setMessage({ type: 'error', text: 'Please enter your Bluesky handle' });
      return;
    }

    if (!blueskyAppPassword.trim()) {
      setMessage({ type: 'error', text: 'Please enter your Bluesky app password' });
      return;
    }

    setVerifyingBluesky(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/social/bluesky/verify',
        { 
          handle: blueskyHandle,
          appPassword: blueskyAppPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessage({ type: 'success', text: response.data.message });
      setBlueskyHandle('');
      setBlueskyAppPassword('');
      fetchSettings();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to verify Bluesky account'
      });
    } finally {
      setVerifyingBluesky(false);
    }
  };

  const unlinkAccount = async (platform) => {
    if (!window.confirm(`Are you sure you want to unlink your ${platform} account?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint =
        platform === 'steam'
          ? '/api/settings/unlink-steam'
          : platform === 'twitch'
          ? '/api/twitch/account/unlink'
          : `/api/social/${platform}/unlink`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: `${platform} account unlinked successfully` });
      fetchSettings();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || `Failed to unlink ${platform} account`
      });
    }
  };

  const linkSocialAccount = async (platform) => {
    const token = localStorage.getItem('token');
    
    // Twitch uses a different API endpoint that returns authUrl
    if (platform === 'twitch') {
      try {
        const response = await axios.get('/api/twitch/oauth/initiate', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success && response.data.authUrl) {
          window.location.href = response.data.authUrl;
        } else {
          setMessage({ type: 'error', text: 'Failed to initiate Twitch authorization' });
        }
      } catch (error) {
        setMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Failed to connect to Twitch' 
        });
      }
      return;
    }
    
    // For other platforms (Discord, Twitter)
    // Store token in cookie for OAuth callback
    document.cookie = `token=${token}; path=/; max-age=3600`;
    window.location.href = `/api/social/${platform}?token=${token}`;
  };

  const toggleVerifiedAccountsVisibility = async () => {
    try {
      const token = localStorage.getItem('token');
      const newValue = !settings.verifiedAccountsPublic;
      
      const response = await axios.put(
        '/api/settings/verified-accounts-visibility',
        { verifiedAccountsPublic: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSettings(prev => ({
        ...prev,
        verifiedAccountsPublic: newValue
      }));
      
      setMessage({ 
        type: 'success', 
        text: response.data.message || `Verified accounts are now ${newValue ? 'public' : 'private'}` 
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update privacy settings'
      });
    }
  };

  const handleDeleteAccount = async (password) => {
    if (!window.confirm('Are you absolutely sure? This action cannot be undone and all your data will be permanently deleted.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/settings/delete-account', {
        headers: { Authorization: `Bearer ${token}` },
        data: { password }
      });

      // Clear local storage and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setMessage({ type: 'success', text: 'Your account has been deleted. Redirecting...' });
      
      setTimeout(() => {
        navigate('/');
        window.location.reload(); // Force reload to clear auth context
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete account'
      });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validate passwords
    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordFormData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordFormData.newPassword)) {
      setMessage({ type: 'error', text: 'Password must contain uppercase, lowercase, and a number' });
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        '/api/settings/change-password',
        {
          currentPassword: passwordFormData.currentPassword,
          newPassword: passwordFormData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: response.data.message || 'Password changed successfully!' });
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to change password'
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setUpdatingEmail(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        '/api/settings/update-email',
        { newEmail: newEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: response.data.message || 'Email updated successfully!' });
      setNewEmail('');
      fetchSettings(); // Refresh settings to show new email
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update email'
      });
    } finally {
      setUpdatingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>⚙️ Account Settings</h1>
        <p>Manage your account verification and security settings</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })} className="close-btn">
            ×
          </button>
        </div>
      )}

      {/* Theme Selector */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🎨 Theme Selection</h2>
        </div>
        <div className="verification-card">
          <div className="verification-info">
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Choose your website theme. Unlock exclusive themes from the Shop!
            </p>
            <div style={{ marginBottom: '12px' }}>
              <label htmlFor="theme-select" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '600',
                color: 'var(--text)'
              }}>
                Active Theme
              </label>
              <select
                id="theme-select"
                value={theme}
                onChange={async (e) => {
                  const newTheme = e.target.value;
                  setChangingTheme(true);
                  try {
                    await changeTheme(newTheme);
                    setMessage({ type: 'success', text: `Theme changed to ${availableThemes.find(t => t.id === newTheme)?.name || newTheme}!` });
                  } catch (error) {
                    setMessage({ type: 'error', text: 'Failed to change theme' });
                  } finally {
                    setChangingTheme(false);
                  }
                }}
                disabled={changingTheme}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)',
                  cursor: 'pointer'
                }}
              >
                {availableThemes.length > 0 ? (
                  availableThemes.map(t => (
                    <option key={t.id} value={t.id} disabled={!t.purchased}>
                      {t.preview ? `${t.preview} ` : ''}{t.name} {!t.purchased ? '🔒' : ''}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="light">☀️ Light Mode</option>
                    <option value="dark">🌙 Dark Mode</option>
                  </>
                )}
              </select>
            </div>
            {changingTheme && (
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                Applying theme...
              </p>
            )}
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '12px' }}>
              💡 Visit the <a href="/shop" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Shop</a> to unlock exclusive premium themes!
            </p>
          </div>
        </div>
      </div>

      {/* Email Verification */}
      <div className="settings-section">
        <div className="section-header">
          <h2>📧 Email Verification</h2>
        </div>
        <div className="verification-card">
          <div className="verification-info">
            <p>
              <strong>Email:</strong> {settings.email}
            </p>
            <p className="status">
              Status:{' '}
              {settings.emailVerified ? (
                <span className="verified">✓ Verified</span>
              ) : (
                <span className="unverified">✗ Not Verified</span>
              )}
            </p>
          </div>
          {!settings.emailVerified && (
            <button
              onClick={resendVerificationEmail}
              disabled={verifyingEmail}
              className="btn-primary"
            >
              {verifyingEmail ? 'Sending...' : 'Resend Verification Email'}
            </button>
          )}
        </div>

        {/* Update Email for Twitter OAuth Users */}
        {settings.email?.includes('@gamersunion.local') && (
          <div className="verification-card" style={{ marginTop: '20px', background: 'var(--warning-bg)', borderLeft: '4px solid var(--warning)' }}>
            <h3 style={{ marginBottom: '12px', color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚠️ Update Your Email
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              You're currently using a placeholder email because you logged in via Twitter. 
              Update to a real email address to receive notifications and account updates.
            </p>
            
            <form onSubmit={handleEmailUpdate}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  New Email Address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  disabled={updatingEmail}
                  className="input-field"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={updatingEmail}
                style={{ width: '100%' }}
              >
                {updatingEmail ? 'Updating Email...' : 'Update Email'}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Account Type Change */}
      <div className="settings-section">
        <div className="section-header">
          <h2>👤 Account Type</h2>
        </div>
        <div className="verification-card">
          <div className="verification-info" style={{ marginBottom: '20px' }}>
            <p>
              <strong>Current Account Type:</strong>{' '}
              {settings.accountType === 'indie_dev' && '🎮 Indie Developer'}
              {settings.accountType === 'content_creator' && '🎥 Content Creator'}
              {settings.accountType === 'giveaway_host' && '🎁 Giveaway Host'}
              {settings.accountType === 'steam_curator' && '🎯 Steam Curator'}
              {settings.accountType === 'regular' && '👤 Regular User'}
              {settings.accountType === 'admin' && '🛡️ Admin'}
            </p>
          </div>

          {pendingRequest ? (
            <div style={{
              padding: '20px',
              background: 'var(--hover-bg)',
              borderRadius: '8px',
              border: '2px solid var(--primary)'
            }}>
              <h3 style={{ marginBottom: '10px', color: 'var(--primary)' }}>⏳ Pending Request</h3>
              <p style={{ marginBottom: '8px' }}>
                <strong>Requested Type:</strong>{' '}
                {pendingRequest.requestedAccountType === 'indie_dev' && '🎮 Indie Developer'}
                {pendingRequest.requestedAccountType === 'content_creator' && '🎥 Content Creator'}
                {pendingRequest.requestedAccountType === 'giveaway_host' && '🎁 Giveaway Host'}
                {pendingRequest.requestedAccountType === 'steam_curator' && '🎯 Steam Curator'}
                {pendingRequest.requestedAccountType === 'regular' && '👤 Regular User'}
              </p>
              <p style={{ marginBottom: '8px' }}>
                <strong>Reason:</strong> {pendingRequest.reason}
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '10px' }}>
                Your request is being reviewed by an admin. You'll be notified when it's processed.
              </p>
            </div>
          ) : (
            <>
              <p className="help-text" style={{ marginBottom: '20px' }}>
                Request to change your account type. Account types help identify your role in the community:
              </p>
              <ul style={{ marginLeft: '20px', marginBottom: '20px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                <li><strong>👤 Regular User:</strong> Standard community member</li>
                <li><strong>🎮 Indie Developer:</strong> Game developers and creators</li>
                <li><strong>🎥 Content Creator:</strong> Streamers, YouTubers, and content creators</li>
                <li><strong>🎁 Giveaway Host:</strong> Create and manage giveaways</li>
                <li><strong>🎯 Steam Curator:</strong> Steam curators and game reviewers</li>
              </ul>

              <form onSubmit={handleAccountTypeChangeSubmit}>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Requested Account Type
                  </label>
                  <select
                    value={accountTypeRequest.requestedAccountType}
                    onChange={(e) => setAccountTypeRequest({ ...accountTypeRequest, requestedAccountType: e.target.value })}
                    required
                    disabled={submittingAccountTypeChange}
                    className="input-field"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--input-bg)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="">Select account type...</option>
                    <option value="regular">👤 Regular User</option>
                    <option value="indie_dev">🎮 Indie Developer</option>
                    <option value="content_creator">🎥 Content Creator</option>
                    <option value="giveaway_host">🎁 Giveaway Host</option>
                    <option value="steam_curator">🎯 Steam Curator</option>
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Reason for Change
                  </label>
                  <textarea
                    value={accountTypeRequest.reason}
                    onChange={(e) => setAccountTypeRequest({ ...accountTypeRequest, reason: e.target.value })}
                    placeholder="Please explain why you're requesting this account type change (minimum 20 characters)..."
                    required
                    disabled={submittingAccountTypeChange}
                    rows="4"
                    maxLength="500"
                    className="input-field"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--input-bg)',
                      color: 'var(--text-primary)',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                    {accountTypeRequest.reason.length}/500 characters (minimum 20)
                  </p>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submittingAccountTypeChange}
                  style={{ width: '100%' }}
                >
                  {submittingAccountTypeChange ? 'Submitting Request...' : 'Submit Request'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Password Change */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🔐 Change Password</h2>
        </div>
        <div className="verification-card">
          <p className="help-text" style={{ marginBottom: '20px' }}>
            Update your password to keep your account secure.
          </p>
          
          <form onSubmit={handlePasswordChange}>
            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Current Password
              </label>
              <input
                type="password"
                value={passwordFormData.currentPassword}
                onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
                placeholder="Enter current password"
                required
                disabled={changingPassword}
                className="input-field"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                New Password
              </label>
              <input
                type="password"
                value={passwordFormData.newPassword}
                onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
                placeholder="Enter new password"
                required
                disabled={changingPassword}
                className="input-field"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '5px' }}>
                Must be at least 8 characters with uppercase, lowercase, and a number
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordFormData.confirmPassword}
                onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                required
                disabled={changingPassword}
                className="input-field"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={changingPassword}
              style={{ marginBottom: '10px' }}
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>

          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '15px' }}>
            Forgot your password?{' '}
            <Link to="/forgot-password" style={{ color: 'var(--link-color)', textDecoration: 'underline' }}>
              Reset it here
            </Link>
          </p>
        </div>
      </div>

      {/* Steam Verification */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🎮 Steam Verification</h2>
        </div>
        {settings.socialAccounts?.steam?.verified ? (
          <div className="verification-card verified-account">
            <div className="account-info">
              <div className="account-avatar">
                <span className="platform-icon">🎮</span>
              </div>
              <div className="account-details">
                <h3>{settings.socialAccounts.steam.username}</h3>
                <p>Steam ID: {settings.socialAccounts.steam.id}</p>
                <p className="verified-date">
                  ✓ Verified on{' '}
                  {new Date(settings.socialAccounts.steam.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button onClick={() => unlinkAccount('steam')} className="btn-danger">
              Unlink Account
            </button>
          </div>
        ) : (
          <div className="verification-card">
            <p className="help-text">
              Enter your Steam ID to verify your account. You can find your Steam ID in your
              profile URL or using sites like{' '}
              <a
                href="https://steamid.io/"
                target="_blank"
                rel="noopener noreferrer"
              >
                steamid.io
              </a>
            </p>
            <div className="input-group">
              <input
                type="text"
                value={steamId}
                onChange={(e) => setSteamId(e.target.value)}
                placeholder="Enter your Steam ID (e.g., 76561198012345678)"
                className="input-field"
              />
              <button
                onClick={verifySteamAccount}
                disabled={verifyingSteam}
                className="btn-primary"
              >
                {verifyingSteam ? 'Verifying...' : 'Verify Steam'}
              </button>
            </div>
            <p className="help-text-small">
              Or use Steam OpenID to automatically verify:
            </p>
            <button onClick={() => linkSocialAccount('steam')} className="btn-oauth steam">
              <span className="oauth-icon">🎮</span> Sign in with Steam
            </button>
          </div>
        )}
      </div>

      {/* Discord Verification */}
      <div className="settings-section">
        <div className="section-header">
          <h2>💬 Discord Verification</h2>
        </div>
        {settings.socialAccounts?.discord?.verified ? (
          <div className="verification-card verified-account">
            <div className="account-info">
              <div className="account-avatar discord">
                <span className="platform-icon">💬</span>
              </div>
              <div className="account-details">
                <h3>
                  {settings.socialAccounts.discord.username}#
                  {settings.socialAccounts.discord.discriminator}
                </h3>
                <p>Discord ID: {settings.socialAccounts.discord.id}</p>
                <p className="verified-date">
                  ✓ Verified on{' '}
                  {new Date(settings.socialAccounts.discord.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button onClick={() => unlinkAccount('discord')} className="btn-danger">
              Unlink Account
            </button>
          </div>
        ) : (
          <div className="verification-card">
            <p className="help-text">
              Link your Discord account to verify your identity and prevent impersonation.
            </p>
            <button onClick={() => linkSocialAccount('discord')} className="btn-oauth discord">
              <span className="oauth-icon">💬</span> Connect Discord
            </button>
          </div>
        )}
      </div>

      {/* Twitter Verification */}
      <div className="settings-section" ref={twitterSectionRef}>
        <div className="section-header">
          <h2>🐦 Twitter Verification</h2>
        </div>
        {settings.socialAccounts?.twitter?.verified ? (
          <div className="verification-card verified-account">
            <div className="account-info">
              <div className="account-avatar twitter">
                <span className="platform-icon">🐦</span>
              </div>
              <div className="account-details">
                <h3>{settings.socialAccounts.twitter.displayName}</h3>
                <p>@{settings.socialAccounts.twitter.username}</p>
                <p className="verified-date">
                  ✓ Verified on{' '}
                  {new Date(settings.socialAccounts.twitter.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button onClick={() => unlinkAccount('twitter')} className="btn-danger">
              Unlink Account
            </button>
          </div>
        ) : (
          <div className="verification-card">
            <p className="help-text">
              Link your Twitter account to verify your identity and prevent impersonation.
            </p>
            <button onClick={() => linkSocialAccount('twitter')} className="btn-oauth twitter">
              <span className="oauth-icon">🐦</span> Connect Twitter
            </button>
          </div>
        )}
      </div>

      {/* Bluesky Verification */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🦋 Bluesky Verification</h2>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
            (Alternative to Twitter)
          </span>
        </div>
        {settings.socialAccounts?.bluesky?.verified ? (
          <div className="verification-card verified-account">
            <div className="account-info">
              <div className="account-avatar bluesky">
                <span className="platform-icon">🦋</span>
              </div>
              <div className="account-details">
                <h3>{settings.socialAccounts.bluesky.displayName}</h3>
                <p>@{settings.socialAccounts.bluesky.handle}</p>
                <p className="verified-date">
                  ✓ Verified on{' '}
                  {new Date(settings.socialAccounts.bluesky.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button onClick={() => unlinkAccount('bluesky')} className="btn-danger">
              Unlink Account
            </button>
          </div>
        ) : (
          <div className="verification-card">
            <p className="help-text" style={{ marginBottom: '15px' }}>
              Verify your Bluesky account. You can use either Twitter or Bluesky for social verification.
            </p>
            
            <div style={{ 
              background: 'var(--hover-bg)', 
              padding: '15px', 
              borderRadius: '8px',
              marginBottom: '15px',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)'
            }}>
              <strong style={{ color: 'var(--text)' }}>📝 How to get an App Password:</strong>
              <ol style={{ marginLeft: '20px', marginTop: '8px', lineHeight: '1.6' }}>
                <li>Go to <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Bluesky Settings → App Passwords</a></li>
                <li>Click "Add App Password"</li>
                <li>Name it "GamersUnion" and create it</li>
                <li>Copy the generated password and paste it below</li>
              </ol>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="bluesky-handle" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '600',
                color: 'var(--text)'
              }}>
                Bluesky Handle
              </label>
              <input
                id="bluesky-handle"
                type="text"
                value={blueskyHandle}
                onChange={(e) => setBlueskyHandle(e.target.value)}
                placeholder="username.bsky.social"
                disabled={verifyingBluesky}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  backgroundColor: 'var(--input-bg)',
                  color: 'var(--text)'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="bluesky-password" style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontWeight: '600',
                color: 'var(--text)'
              }}>
                App Password
              </label>
              <input
                id="bluesky-password"
                type="password"
                value={blueskyAppPassword}
                onChange={(e) => setBlueskyAppPassword(e.target.value)}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                disabled={verifyingBluesky}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid var(--border)',
                  backgroundColor: 'var(--input-bg)',
                  color: 'var(--text)'
                }}
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                We never store your app password - it's only used for verification
              </small>
            </div>

            <button 
              onClick={verifyBlueskyAccount}
              disabled={verifyingBluesky || !blueskyHandle || !blueskyAppPassword}
              className="btn-oauth bluesky"
              style={{
                opacity: (verifyingBluesky || !blueskyHandle || !blueskyAppPassword) ? 0.6 : 1,
                cursor: (verifyingBluesky || !blueskyHandle || !blueskyAppPassword) ? 'not-allowed' : 'pointer'
              }}
            >
              <span className="oauth-icon">🦋</span> 
              {verifyingBluesky ? 'Verifying...' : 'Verify Bluesky Account'}
            </button>
          </div>
        )}
      </div>

      {/* Twitch Verification */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🎮 Twitch Verification</h2>
        </div>
        {settings.socialAccounts?.twitch?.verified ? (
          <div className="verification-card verified-account">
            <div className="account-info">
              <div className="account-avatar twitch">
                <span className="platform-icon">🎮</span>
              </div>
              <div className="account-details">
                <h3>{settings.socialAccounts.twitch.displayName}</h3>
                <p>@{settings.socialAccounts.twitch.username}</p>
                <p className="verified-date">
                  ✓ Verified on{' '}
                  {new Date(settings.socialAccounts.twitch.verifiedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <button onClick={() => unlinkAccount('twitch')} className="btn-danger">
              Unlink Account
            </button>
          </div>
        ) : (
          <div className="verification-card">
            <p className="help-text">
              Link your Twitch account to use the Twitch Chat Picker tool and collect chat entries automatically.
            </p>
            <button onClick={() => linkSocialAccount('twitch')} className="btn-oauth twitch">
              <span className="oauth-icon">🎮</span> Connect Twitch
            </button>
          </div>
        )}
      </div>

      {/* Privacy Settings */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🔒 Privacy Settings</h2>
        </div>
        <div className="verification-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>Verified Accounts Visibility</h3>
              <p className="help-text" style={{ margin: 0 }}>
                {settings.verifiedAccountsPublic 
                  ? 'Your verified accounts are public and clickable on your profile'
                  : 'Your verified accounts are private (badges shown but links disabled)'}
              </p>
            </div>
            <button 
              onClick={toggleVerifiedAccountsVisibility}
              className="btn-primary"
              style={{
                padding: '10px 20px',
                minWidth: '100px',
                background: settings.verifiedAccountsPublic ? 'var(--accent-green)' : 'var(--text-secondary)'
              }}
            >
              {settings.verifiedAccountsPublic ? '👁️ Public' : '🔒 Private'}
            </button>
          </div>
          <div style={{
            padding: '15px',
            background: 'var(--hover-bg)',
            borderRadius: '8px',
            marginTop: '15px'
          }}>
            <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              <strong>Public:</strong> Verified account badges on your profile are clickable and redirect to your Steam, Discord, or Twitter profiles.
              <br />
              <strong>Private:</strong> Verified badges are displayed but not clickable, providing verification without exposing direct links.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="settings-section">
        <div className="section-header">
          <h2>⚠️ Danger Zone</h2>
        </div>
        <div className="verification-card" style={{ border: '2px solid var(--error-text)', backgroundColor: 'rgba(255, 71, 87, 0.05)' }}>
          <h3 style={{ color: 'var(--error-text)', marginBottom: '10px' }}>Delete Account</h3>
          <p className="help-text" style={{ marginBottom: '15px' }}>
            Once you delete your account, there is no going back. This action will:
          </p>
          <ul style={{ marginLeft: '20px', marginBottom: '20px', color: 'var(--text-secondary)' }}>
            <li>Permanently delete all your giveaways and entries</li>
            <li>Remove all your reviews, comments, and community cards</li>
            <li>Delete all your messages and conversations</li>
            <li>Remove you from other users' followers lists</li>
            <li>Permanently erase all your account data</li>
          </ul>
          <button 
            onClick={() => {
              const password = prompt('To delete your account, please enter your password:');
              if (password) {
                handleDeleteAccount(password);
              }
            }}
            className="btn-danger"
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            🗑️ Delete My Account
          </button>
        </div>
      </div>

      {/* Security Notice */}
      <div className="settings-section security-notice">
        <h3>🔐 Security Notice</h3>
        <ul>
          <li>You will receive an email notification whenever a social account is linked or verified</li>
          <li>Verified accounts help prevent impersonation and build trust in the community</li>
          <li>Your social account information is securely stored and never shared without permission</li>
          <li>You can unlink any account at any time</li>
        </ul>
      </div>
    </div>
  );
};

export default Settings;
